import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { connect } from "react-redux";
import { createCheckoutApi } from "../../../services/api.services";
import { checkoutIdParamUrl } from "../../../helper/constant";
import { getOrderStoreId, buildSlotPayload } from "../../../helper/checkoutStore";
import { setCheckoutStep } from "../../../redux/checkout/action";

const STRIPE_JS = "https://js.stripe.com/v3/";

// Carica js.stripe.com una sola volta e risolve window.Stripe.
function loadStripeJs() {
  return new Promise((resolve, reject) => {
    if (window.Stripe) return resolve(window.Stripe);
    const existing = document.querySelector(`script[src="${STRIPE_JS}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.Stripe));
      existing.addEventListener("error", reject);
      return;
    }
    const s = document.createElement("script");
    s.src = STRIPE_JS;
    s.async = true;
    s.onload = () => resolve(window.Stripe);
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

// Costruisce il payload per create-checkout dallo stato redux del checkout.
// L'id_param_url e' quello fresco salvato al "Procedi" dello step 1 (opzione A).
function buildCreateCheckoutPayload(checkout) {
  const {
    deliveryMethod,
    pickupDate,
    selectedSlot,
    shippingData = {},
    paymentMethod,
  } = checkout || {};

  const isPickup = deliveryMethod === "pickup";

  const storeId = getOrderStoreId(checkout);

  const shipping = {
    nome: shippingData.nome,
    cognome: shippingData.cognome,
    email: shippingData.email,
    telefono: shippingData.telefono,
  };
  if (deliveryMethod === "delivery") {
    shipping.indirizzo = shippingData.indirizzo || "";
    shipping.citta = shippingData.citta || "";
    shipping.cap = shippingData.cap || "";
    shipping.note = shippingData.note || "";
  }

  // URL di ritorno tenant-aware: siamo su .../<client>/<slug>/checkout ->
  // sostituisco l'ultimo segmento per ottenere la base del tenant.
  const base =
    window.location.origin + window.location.pathname.replace(/\/[^/]*$/, "");

  return {
    id_param_url: localStorage.getItem(checkoutIdParamUrl),
    payment_method: paymentMethod,
    ui_mode: "embedded",
    delivery: {
      method: deliveryMethod,
      store_id: storeId,
      date: isPickup ? pickupDate || null : null,
      slot: isPickup ? buildSlotPayload(selectedSlot) : null,
    },
    shipping,
    resultUrl: base + "/ordine-confermato",
    cancelUrl: base + "/pagamento-annullato",
  };
}

function StripeEmbeddedCheckout(props) {
  const embedRef = useRef(null); // istanza Stripe Embedded Checkout
  const [state, setState] = useState({ loading: true, error: null, mounted: false });

  const init = async () => {
    setState({ loading: true, error: null, mounted: false });
    try {
      // Pulisci eventuale istanza/DOM precedente (retry o back-navigation)
      if (embedRef.current) {
        try { embedRef.current.destroy?.(); } catch (e) { /* noop */ }
        embedRef.current = null;
      }
      const container = document.getElementById("stripe-checkout");
      if (container) {
        while (container.firstChild) container.removeChild(container.firstChild);
      }

      const Stripe = await loadStripeJs();
      if (!Stripe) throw new Error("Stripe non è stato caricato correttamente");

      // Payload completo: id_param_url (fresco dal Procedi), metodo di consegna,
      // dati spedizione e URL di ritorno. Il backend risolve il carrello dall'id.
      const payload = buildCreateCheckoutPayload(props.checkout);
      const res = await createCheckoutApi(payload);

      const data = res?.data;
      const ok = res?.status === 200 || res?.success;
      if (!ok || !data?.client_secret) {
        throw new Error(res?.message || "Errore nella creazione del checkout");
      }

      const stripe = Stripe(data.publishable_key);
      const embed = await stripe.initEmbeddedCheckout({
        clientSecret: data.client_secret,
      });
      embedRef.current = embed;

      await embed.mount("#stripe-checkout");
      setState({ loading: false, error: null, mounted: true });
    } catch (err) {
      setState({
        loading: false,
        error: err?.message || "Errore durante l'inizializzazione del pagamento",
        mounted: false,
      });
    }
  };

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(() => {
      if (!cancelled) init();
    }, 100);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (embedRef.current) {
        try { embedRef.current.destroy?.(); } catch (e) { /* noop */ }
        embedRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ position: "relative", minHeight: 340 }}>
      {/* Loader durante il caricamento/mount */}
      {state.loading && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            background: "rgba(255,255,255,0.8)",
            zIndex: 2,
            borderRadius: 12,
            color: "#666",
            fontSize: "0.9rem",
          }}
        >
          <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: "1.6rem", color: "#635bff" }} />
          Inizializzazione pagamento…
        </div>
      )}

      {/* Stato di errore con retry */}
      {state.error && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
            padding: "32px 16px",
            textAlign: "center",
          }}
        >
          <p style={{ color: "#dc3545", fontWeight: 600, margin: 0 }}>
            Impossibile caricare il pagamento
          </p>
          <p style={{ color: "#888", fontSize: "0.85rem", margin: 0 }}>
            {state.error}
          </p>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center" }}>
            <button className="up-btn up-btn--outline" onClick={init}>
              Riprova
            </button>
            <button className="up-btn up-btn--ghost" onClick={() => props.setCheckoutStep(2)}>
              Cambia metodo di pagamento
            </button>
          </div>
        </div>
      )}

      {/* Container dell'embed Stripe (React non gestisce il contenuto interno) */}
      <div id="stripe-checkout" style={{ width: "100%" }} />
    </div>
  );
}

const mapStateToProps = (state) => ({ checkout: state.checkout });

export default connect(mapStateToProps, { setCheckoutStep })(StripeEmbeddedCheckout);
